import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { fetch as apiFetch, create as apiCreate } from 'api';
import { showNotification } from 'store/actions/notifications';
import { NOTIFICATION_TYPE_ERROR } from 'constants/notification';

// Venda que nasce antes do cadastro do cliente (22/10/2026): o vendedor
// digita o número do negócio do Pipedrive, confere nome/e-mail/PT e a venda
// é criada sem cliente. Ele monta os passos e, no fim, envia o link para o
// cliente preencher os dados. O fluxo antigo (link do Libertadores) segue igual.

const vazio = { name: '', email: '', phone: '', clientId: '' };

const mensagemDoErro = (error) => (
  (error && error.response && error.response.data && error.response.data.message)
  || 'Não foi possível concluir. Tente de novo.'
);

function NewSaleFromDeal({ notify }) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [dealId, setDealId] = useState('');
  const [deal, setDeal] = useState(null);
  const [form, setForm] = useState(vazio);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setDealId('');
    setDeal(null);
    setForm(vazio);
    setExisting(null);
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const buscar = (e) => {
    if (e) e.preventDefault();
    const numero = `${dealId}`.replace(/\D/g, '');
    if (!numero) return;
    setLoading(true);
    setError('');
    setExisting(null);
    apiFetch(`salesscope/deal/${numero}`)
      .then((res) => {
        setDeal(res.deal);
        setExisting(res.existing_scope);
        setForm({
          name: res.deal.person.name || '',
          email: res.deal.person.email || '',
          phone: res.deal.person.phone || '',
          clientId: res.deal.pt || '',
        });
      })
      .catch((err) => {
        setDeal(null);
        setError(mensagemDoErro(err));
      })
      .finally(() => setLoading(false));
  };

  const criar = () => {
    setLoading(true);
    setError('');
    apiCreate('salesscope/from-deal', {
      deal_id: deal.deal_id,
      client_id: form.clientId.trim(),
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
    })
      .then((res) => {
        handleClose();
        history.push(`/nova-venda/${res.scope.id}`);
      })
      .catch((err) => {
        const data = err && err.response && err.response.data;
        if (data && data.existing_scope) setExisting(data.existing_scope);
        setError(mensagemDoErro(err));
        notify(NOTIFICATION_TYPE_ERROR, mensagemDoErro(err));
        setLoading(false);
      });
  };

  const change = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpen(true)}
        style={{ marginRight: 16, height: 40, whiteSpace: 'nowrap' }}
        startIcon={<Add />}
      >
        Nova venda
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Nova venda pelo negócio do Pipedrive</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Digite o número do negócio. A venda é criada com os dados da pessoa no Pipedrive;
            no fim você envia um link para o cliente preencher o cadastro e assinar.
          </DialogContentText>
          <form onSubmit={buscar}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  autoFocus
                  fullWidth
                  variant="outlined"
                  label="Número do negócio"
                  value={dealId}
                  onChange={(e) => setDealId(e.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={4}>
                <Button fullWidth variant="outlined" type="submit" disabled={loading || !dealId}>
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </form>
          {loading && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <CircularProgress size={28} />
            </div>
          )}
          {deal && (
            <Grid container spacing={2} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  {`Negócio ${deal.deal_id}${deal.title ? ` · ${deal.title}` : ''}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  label="PT da venda"
                  value={form.clientId}
                  onChange={change('clientId')}
                  error={!form.clientId}
                  helperText={deal.pt_valid ? 'Nome da organização no Pipedrive' : 'Confira o PT: a organização não está no formato PT…'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth variant="outlined" label="Nome do cliente" value={form.name} onChange={change('name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth variant="outlined" label="E-mail do cliente" value={form.email} onChange={change('email')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth variant="outlined" label="Telefone" value={form.phone} onChange={change('phone')} />
              </Grid>
            </Grid>
          )}
          {existing && (
            <Typography color="error" style={{ marginTop: 16 }}>
              {`Já existe uma venda com o PT ${existing.client_id}. `}
              <Button
                size="small"
                color="primary"
                onClick={() => { handleClose(); history.push(`/nova-venda/${existing.id}`); }}
              >
                Abrir venda
              </Button>
            </Typography>
          )}
          {error && !existing && (
            <Typography color="error" style={{ marginTop: 16 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={criar}
            disabled={!deal || loading || !form.clientId || Boolean(existing)}
          >
            Criar venda
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default connect(null, { notify: showNotification })(NewSaleFromDeal);
